import { Fragment, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { RootState } from "../../context/store";
import Table1 from "src/components/ui/table1.component";
import UinRequestsDetails from "../../container/uin-requests-details-form.component";
import { Transition } from "@headlessui/react";
import { FilterIcon } from "lucide-react";
import { usePagination } from "src/hooks/usePagination";
import Button from "../../components/Button";
import Spinner from "../../components/Spinner";
import ArrowIcon from "src/assets/down-arrow.svg";
import CountersFilter from "../../container/CounterFilter";
import IndphUinReqStatusForm from "./IndphUinReqStatusForm";
import Modal from "../../components/Modal";
import { craActions } from "../../context/cra-slice";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { getMasterUINDetails, postCounterRquestMatching } from "../../services/cda-api";
import { validateUin } from "src/services/counter-api";
import { queryDivisionDetails } from "../../services/division-api";
import { getDistrictDetails } from "src/services/region-api";
import { verifyMobileNumber, fetchMobileVerificationStatus } from "src/services/employee-api";
import { appendParams, extractFileNameAndExtension } from "../../utils/utils";
import { getCookieValue } from "src/constants/functions";
import { useToast } from "src/hooks/useToast";
import UinDetails from "./uin-details.component";

const HomeIndphUinValidateTab = () => {
  const auth: any = useSelector((state: any) => state.auth);
  const [loading, setLoading] = useState(false);
  const [companyUINData, setCompanyUINData] = useState<Record<string, any>>({});
  const [reqUpdateData, setReqUpdateData] = useState<Record<string, any>>({});
  const [tableData, setTableData] = useState([]);
  const [matchingStatus, setMatchingStatus] = useState<{ [key: string]: boolean }>({});
  const { pagination, onPaginationChange, pageIndex, pageSize, resetPagination } = usePagination();
  const [globalSearch, setGlobalSearch] = useState("");
  const [totalRowCount, setTotalRowCount] = useState(10);
  const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
  const [mobileVerificationInfo, setMobileVerificationInfo] = useState<Record<string, any>>({});
  const [isUinDetailsOpen, setIsUinDetailsOpen] = useState<boolean>(false);
  const [validationResponse, setValidationResponse] = useState<any>({});
  const [verifiedUinReqId, setVerifiedUinReqId] = useState<any>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const { craFiltersData, UinFilters } = useSelector((state: any) => state.cra);
  const { currentPsr } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  // const navigate = useNavigate();

  const { toast } = useToast();

  const ref = useOutsideClick(() => {
    setIsFiltersShown(false);
  });

  const [_, setSearchParams] = useSearchParams();

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor("uin_counterName", {
        cell: (info: any) => (
          <div className="flex gap-10 items-center">
            <div className="flex py-3 items-center">
              <span className="font-semibold text-base text-black">CREQINDPH-{info.row.original.indph_uln_validation}</span>
            </div>
            <div className="h-5 w-0.5 bg-gray-300"></div>
            <div className="flex py-3 items-center">
              <span className="font-semibold text-base text-black">{info.row.original.counter_code}</span>
              <span className="mx-1 text-black">-</span>
              <span className="font-semibold text-base text-black">{info.row.original.ass_counter_name}</span>
            </div>
            <div className="h-5 w-0.5 bg-gray-300"></div>
            <div className="flex py-3 items-center">
              <span className="font-semibold text-base text-black">
                {info.row.original?.divisions?.[0]?.company_division_code?.company_code?.name}
              </span>
              <span className="mx-1 text-black">-</span>
              <span className="font-semibold text-base text-black">{info.row.original?.divisions?.[0]?.company_division_code?.division_name}</span>
            </div>
            <div className="h-5 w-0.5 bg-gray-300"></div>
            <span
              className="font-semibold text-base text-black"
              title={new Date(info.row.original.modified_date).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })}
            >
              {new Date(info.row.original.modified_date).toLocaleDateString("en-GB").replace(/\//g, "-")}
            </span>
          </div>
        ),
      }),
      columnHelper.accessor("match_counter", {
        enableSorting: false,
        cell: (info: any) => {
          const isExpanded = info.row.getIsExpanded();

          if (isExpanded) {
            return (
              <div className="flex justify-end">
                <Button
                  onClick={() => handleCounterMatch(info.row.original)}
                  isLoading={!!matchingStatus[info.row.original?.indph_uln_validation]}
                  loaderSize="small"
                  className="gap-2 text-sm border"
                >
                  Match Counter
                </Button>
              </div>
            );
          }
          return null;
        },
      }),
      columnHelper.accessor("expand_toggle", {
        enableSorting: false,
        cell: (info: any) => {
          const rowId = info.row.original?.indph_uln_validation;

          // Check if the row is currently expanded using React Table's method
          const isExpanded = info.row.getIsExpanded();

          const handleRowToggle = async () => {
            if (isExpanded) {
              info.row.toggleExpanded(false);
            } else {
              fetchMobileVerifyStatus(info.row.original?.ass_contact_number, info.row.original?.indph_uln_validation);
              info.row.toggleExpanded(true);
            }
          };

          return (
            <div className="flex justify-end gap-1 pr-1">
              <button
                onClick={handleRowToggle}
                className="flex items-center justify-center w-8 h-8 p-1.5 border-2 border-[#D0D0D0] rounded-md cursor-pointer bg-[#F1F1F1] shadow-sm"
                disabled={companyUINData?.[rowId]?.isLoading}
              >
                {companyUINData?.[rowId]?.isLoading ? (
                  <Spinner size="small" />
                ) : (
                  <img
                    src={ArrowIcon}
                    alt="Expand/Collapse"
                    className={`w-5 h-5 duration-300 transition-transform ease-in-out ${isExpanded ? "-rotate-180" : "rotate-0"}`}
                  />
                )}
              </button>
            </div>
          );
        },
      }),
    ],
    [tableData, companyUINData, matchingStatus]
  );

  const fetchMobileVerifyStatus = async (mobileNumber: number, reqId: number | string) => {
    try {
      const res: any = await fetchMobileVerificationStatus([mobileNumber]);
      if (!res || res?.status !== 200 || !res.data) {
        throw new Error(res?.error || "Failed to fetch verification status");
      }
      const verificationRes = res?.data?.response;
      setMobileVerificationInfo((prev) => ({
        ...prev,
        [reqId]: {
          loading: false,
          isVerifyReq: verificationRes?.length ? false : true,
          verificationData: verificationRes?.[0] || null,
        },
      }));

      toast({ variant: "default", description: "Mobile verification status fetched successfully" });
    } catch (err: any) {
      console.error("Error fetching mobile verification status:", err.message || err);
      toast({ variant: "destructive", description: "Failed to fetch mobile verification status. Please try again." });
    }
  };

  const handleMobileNumberVerify = async (mobileNumber: number | string, reqId: number | string) => {
    if (!mobileNumber) return;

    try {
      setMobileVerificationInfo((prev) => ({
        ...prev,
        [reqId]: {
          ...prev[reqId],
          loading: true,
        },
      }));
      const idToken = getCookieValue("idToken");

      if (!idToken) {
        throw new Error("User is not authenticated.");
      }

      const response: any = await verifyMobileNumber(mobileNumber, currentPsr?.cognito_id, "Counter Master", idToken);

      if (response?.status === 200) {
        setMobileVerificationInfo((prev) => ({
          ...prev,
          [reqId]: {
            loading: false,
            isVerifyReq: false,
            verificationData: response?.data?.response,
          },
        }));
        toast({ variant: "default", description: "Mobile number verified successfully." });
      } else {
        throw new Error(response?.error || "Unexpected response from server.");
      }
    } catch (error) {
      console.error("Mobile verification failed:", error);
      toast({ variant: "destructive", description: "Failed to verify mobile number. Please try again." });
    } finally {
      setMobileVerificationInfo((prev) => ({
        ...prev,
        [reqId]: {
          ...prev[reqId],
          loading: false,
        },
      }));
    }
  };

  const fetchCompanyUINData = async (uinValidateReq: any) => {
    setVerifiedUinReqId(uinValidateReq?.indph_uln_validation);
    setLoader(true);
    try {
      const token = await getCookieValue("idToken");
      const userUCode = auth?.token?.["custom:ucode"];
      const body = {
        UserUCode: userUCode,
        Company_UIN:
          uinValidateReq?.divisions?.[0]?.company_division_code?.alternate_uin_status === true
            ? uinValidateReq?.alternate_uin_code
            : uinValidateReq?.uin_code,
        Company_DivisionCode: String(uinValidateReq?.company_divisioncode),
        PSR_UCode: uinValidateReq?.uid,
      };

      const res = await validateUin(body, token);
      setIsUinDetailsOpen(true);
      setValidationResponse(res?.data || {});

      if (res?.data?.status_code !== "400" && res?.data?.status === true) {
        toast({ variant: "default", description: "Company UIN data fetched successfully" });
      } else if (res?.data?.status_code !== "400" && res?.data?.status === false) {
        toast({
          description: res?.data?.message || "Error fetching company UIN data",
          variant: "destructive",
        });
      } else {
        throw new Error(res?.data?.message || "Invalid response from server");
      }
    } catch (err: any) {
      toast({
        description: err.message || "Please try again later",
        variant: "destructive",
      });
      throw err;
    } finally {
      setLoader(false);
    }
  };

  const handleUpdateStatusClick = (actionType: string, reqId: number | string) => {
    setReqUpdateData({ actionType: actionType, reqId: reqId });
    setIsUinDetailsOpen(false)
  };

  const handleCounterMatch = async (data: any) => {
    const rowId = data?.indph_uln_validation;
    setMatchingStatus((prev) => ({ ...prev, [rowId]: true }));
    try {
      const responsePayload = {
        Counter_Code: "",
        Counter_Name: data?.ass_counter_name,
        ClinicName: "",
        Address: "",
        Mobile_Number: data?.ass_contact_number,
        City_code: (data?.counter?.city_code).toString(),
        Speciality: "",
        MCI_RegisterNumber: "",
        Phmarmacy_Name: "",
        DrugLicenseNumber: "",
        GSTNumber: "",
        Pharmacy_Address: "",
        Pharmacy_Mobile_Number: "",
        Pharmacy_City_code: "0",
        Company_Code: (data?.company_code ?? 0).toString(),
        Hospital_Name: "",
        Associated_Dr_Name: "",
        Associated_Dr_Mobile_Number: "",
        User_Ucode: "5B60CF25-E636-4843-95B5-3D3175599F5C",
      };

      const token: any = getCookieValue("idToken");

      const res = await postCounterRquestMatching(responsePayload, token);

      if (res?.status !== 200) throw new Error();
      if (!res?.data?.status) {
        toast({
          description: res?.data?.message,
        });
        return;
      }
      if (res?.status === 200 && res?.data?.status === false) {
        toast({ variant: "default", description: res?.data?.message });
      }

      const filePath = res?.data?.filepath;

      const s3Link = filePath;
      const s3Response = await fetch(s3Link);
      const s3Blob = await s3Response.blob();
      const s3Url = window.URL.createObjectURL(s3Blob);
      const s3LinkElement = document.createElement("a");
      s3LinkElement.href = s3Url;
      const [originalFileName, extension] = extractFileNameAndExtension(s3Link);
      const drName = data?.ass_counter_name || originalFileName;
      const downloadFileName = `${drName}.${extension}`;
      s3LinkElement.setAttribute("download", downloadFileName);
      document.body.appendChild(s3LinkElement);
      s3LinkElement.click();
      s3LinkElement.parentNode?.removeChild(s3LinkElement);

      toast({
        description: "Counter Match Report Generated successfully",
        variant: "default",
        duration: 3000,
      });
    } catch (error) {
      console.log(error, "error in counter match");
      toast({
        description: "Error in counter match",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setMatchingStatus((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const fetchDetails = () => {
    setLoading(true);

    setSearchParams({
      pageSize: pageSize.toString(),
      pageIndex: pageIndex.toString(),
    });

    const companyParams = appendParams(Array.isArray(UinFilters?.company) ? UinFilters.company : [], "company_code", "code");
    const divisionParams = appendParams(Array.isArray(UinFilters?.division) ? UinFilters.division : [], "division_id", "company_divisioncode");
    const stateParams = appendParams(Array.isArray(UinFilters?.state) ? UinFilters.state : [], "state_code", "state_code");
    const districtParams = appendParams(Array.isArray(UinFilters?.district) ? UinFilters.district : [], "district_code", "district_code");
    const cityParams = appendParams(Array.isArray(UinFilters?.city) ? UinFilters.city : [], "city_code", "city_code");

    let filtersQuery = [companyParams, divisionParams, stateParams, districtParams, cityParams].join("&");

    filtersQuery.endsWith("&&") && (filtersQuery = filtersQuery.slice(0, -1));
    filtersQuery.endsWith("&") && (filtersQuery = filtersQuery.slice(0, -1));

    getMasterUINDetails("status=pending", pageIndex - 1, filtersQuery, globalSearch)
      .then((res) => {
        setTableData(res?.data?.data?.data || []);
        setTotalRowCount(res?.data?.data?.totalDataCount);
        if (res?.status === 200) {
          toast({ variant: "default", description: "INDPH-UIN requests fetched successfully" });
        } else {
          toast({ variant: "destructive", description: res?.data?.message || "Failed to fetch INDPH-UIN requests" });
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDetails();
  }, [auth?.currentPsr, pageSize, pageIndex]);

  let pageCount = Math.ceil(totalRowCount / (pageSize ?? 1));

  let timerId: any;
  const handleSearch = (searchQuery: string) => {
    if (timerId) clearTimeout(timerId);

    timerId = setTimeout(() => {
      resetPagination();
      setGlobalSearch(searchQuery);
    }, 500);
  };

  useEffect(() => {
    fetchDetails();
  }, [globalSearch]);

  function selectedRowHandler(data: any): void {
    const selectedIds: any[] = [];

    data?.forEach((item: any) => {
      selectedIds.push(item?.original);
    });
  }

  useEffect(() => {
    if (UinFilters?.company?.length) {
      queryDivisionDetails(`/details/?fields=division_name&company_code=${UinFilters?.company[0]?.code}`)
        .then((res) => dispatch(craActions.setCraFiltersData({ key: "uinDivision", value: res.data })))
        .catch((err) => console.log(err));
    } else {
      dispatch(craActions.setCraFiltersData({ key: "uinDivision", value: [] }));
      dispatch(craActions.setUinFilter({ key: "division", value: [] }));
    }
  }, [UinFilters?.company]);

  useEffect(() => {
    if (UinFilters?.state?.length) {
      getDistrictDetails(`?cityOnly=true&state_code=${UinFilters?.state[0]?.state_code || ""}`).then((res) => {
        dispatch(craActions.setCraFiltersData({ key: "uinDistrict", value: res.data.data }));
        const allCities = res.data.data.flatMap((district: any) => district.cities.map((city: any) => city));
        dispatch(craActions.setCraFiltersData({ key: "uinCity", value: allCities }));
      });
    } else {
      dispatch(craActions.setCraFiltersData({ key: "uinDistrict", value: [] }));
      dispatch(craActions.setUinFilter({ key: "district", value: [] }));
      dispatch(craActions.setCraFiltersData({ key: "uinCity", value: [] }));
      dispatch(craActions.setUinFilter({ key: "city", value: [] }));
    }
  }, [UinFilters?.state]);

  return (
    <Fragment>
      <Transition
        as={Fragment}
        show={isFiltersShown}
        enter="transition transition ease-in-out duration-200 transform"
        enterFrom="translate-x-full"
        enterTo="translate-x-0"
        leave="transition  ease-in-out duration-200 transform"
        leaveFrom="translate-x-0"
        leaveTo="translate-x-full"
      >
        <div className="fixed right-0 inset-0 top-0 z-[500]">
          <CountersFilter
            ref={ref}
            isUIN={true}
            companyDetails={craFiltersData?.company || []}
            divisionDetails={craFiltersData?.uinDivision || []}
            stateDetails={craFiltersData?.state || []}
            districtDetails={craFiltersData?.uinDistrict || []}
            cityDetails={craFiltersData?.uinCity || []}
            uinStatus={craFiltersData?.uinStatus || []}
            onCloseClick={() => setIsFiltersShown(false)}
          />
        </div>
      </Transition>
     
        <div className="flex justify-end float-right gap-2">
          <Button
            className="gap-1 px-4 mt-2 ml-2 border "
            onClick={() => {
              setIsFiltersShown(true);
            }}
          >
            Filters <FilterIcon className="w-5 h-5" />
          </Button>
          <Button onClick={fetchDetails} loaderSize="small" className="h-auto py-2 mt-2 border">
            Retrieve
          </Button>
        </div>
      

      <div>
        <Table1
          title="UIN Validation"
          subtitle="View and manage pending counter requests"
          showheader={false}
          isLoading={loading}
          renderSubComponent={(row) => {
            return (
              <UinRequestsDetails
                row={row}
                handleButtonClick={fetchCompanyUINData}
                mobileVerificationInfo={mobileVerificationInfo}
                handleMobileVerification={handleMobileNumberVerify}
                loader={loader}
              />
            );
          }}
          getRowCanExpand={() => true}
          data={tableData ?? []}
          columns={columns}
          isGlobalSearch={true}
          manualPagination={true}
          pageCount={pageCount}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          getSelectedRows={selectedRowHandler}
          onGlobalSearch={handleSearch}
        />
      </div>

      {reqUpdateData?.actionType && reqUpdateData?.reqId && (
        <Modal
          isOpen={true}
          isHeader={true}
          title="Update Status"
          onCloseModal={() => {
            setReqUpdateData({});
          }}
        >
          <IndphUinReqStatusForm
            masterUINDetails={tableData.find((req: any) => req?.indph_uln_validation == reqUpdateData?.reqId)}
            companyUINDetails={validationResponse?.CompanyUINDetails}
            actionType={reqUpdateData?.actionType}
            reqId={reqUpdateData?.reqId}
            onSuccess={() => {
              fetchDetails();
              setReqUpdateData({});
              setCompanyUINData({});
            }}
            onCancel={() => {
            setReqUpdateData({});
          }}
          />
        </Modal>
      )}

      <Modal
        isOpen={isUinDetailsOpen}
        isHeader={true}
        title="Confirm UIN details"
        onCloseModal={() => setIsUinDetailsOpen(false)}
        onBack={() => {
          setIsUinDetailsOpen(false);
        }}
      >
        {validationResponse ? (
          <UinDetails
            uinData={validationResponse}
            uinReqData={tableData?.find((req: any) => req?.indph_uln_validation === verifiedUinReqId)}
            handleButtonClick={handleUpdateStatusClick}
          />
        ) : (
          <>{alert("No response of UIN validation found")}</>
        )}
      </Modal>
    </Fragment>
  );
};

export default HomeIndphUinValidateTab;
